/**
 * Admin: Short Links CRUD — self-hosted URL shortener.
 * Route: /admin/short-links
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, Trash2, ExternalLink, Power, Search, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import AdminLayout from "@/components/app/AdminLayout";
import api from "@/lib/api";

export default function ShortLinks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [copiedSlug, setCopiedSlug] = useState(null);

  // Create form
  const [longUrl, setLongUrl] = useState("");
  const [customSlug, setCustomSlug] = useState("");
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/short-links");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("שגיאה בטעינת הקיצורים");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!longUrl.trim()) {
      toast.error("חסר URL יעד");
      return;
    }
    setCreating(true);
    try {
      const payload = { long_url: longUrl.trim() };
      if (customSlug.trim()) payload.slug = customSlug.trim();
      if (title.trim()) payload.title = title.trim();
      const { data } = await api.post("/admin/short-links", payload);
      setItems((prev) => [data, ...prev]);
      setLongUrl("");
      setCustomSlug("");
      setTitle("");
      toast.success(`נוצר קיצור: /s/${data.slug}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "שגיאה ביצירת הקיצור";
      toast.error(typeof msg === "string" ? msg : "שגיאה ביצירת הקיצור");
    } finally {
      setCreating(false);
    }
  };

  const onCopy = async (url, slug) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      toast.success("הועתק ללוח");
      setTimeout(() => setCopiedSlug(null), 1500);
    } catch (_) {
      toast.error("ההעתקה נכשלה — העתיקו ידנית");
    }
  };

  const onToggleActive = async (link) => {
    try {
      const { data } = await api.put(`/admin/short-links/${link.id}`, {
        is_active: !link.is_active,
      });
      setItems((prev) => prev.map((x) => (x.id === link.id ? data : x)));
      toast.success(data.is_active ? "הקיצור הופעל" : "הקיצור הושבת");
    } catch (_) {
      toast.error("העדכון נכשל");
    }
  };

  const onDelete = async (link) => {
    if (!window.confirm(`למחוק את הקיצור /s/${link.slug}?`)) return;
    try {
      await api.delete(`/admin/short-links/${link.id}`);
      setItems((prev) => prev.filter((x) => x.id !== link.id));
      toast.success("הקיצור נמחק");
    } catch (_) {
      toast.error("המחיקה נכשלה");
    }
  };

  const filtered = items.filter((x) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      x.slug.toLowerCase().includes(q) ||
      (x.long_url || "").toLowerCase().includes(q) ||
      (x.title || "").toLowerCase().includes(q)
    );
  });

  const totalClicks = items.reduce((sum, x) => sum + (x.click_count || 0), 0);

  return (
    <AdminLayout title="קיצורי קישורים">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <Stat label="קיצורים פעילים" value={items.filter((x) => x.is_active).length} testId="stat-active" />
        <Stat label="סה״כ קליקים" value={totalClicks} testId="stat-clicks" />
        <Stat label="סה״כ קיצורים" value={items.length} testId="stat-total" />
      </div>

      {/* Create form */}
      <form
        onSubmit={onCreate}
        className="bg-white rounded-2xl border border-stone-200 p-5 md:p-6 mb-6 shadow-sm"
        data-testid="short-link-form"
      >
        <h2 className="text-lg font-[800] mb-4 flex items-center gap-2 text-brand-charcoal">
          <Plus className="w-5 h-5 text-brand-amber" />
          צור קיצור חדש
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="long_url" className="text-sm font-bold text-stone-700 mb-1.5 block">
              קישור יעד <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="long_url"
              data-testid="short-link-long-url"
              type="url"
              value={longUrl}
              onChange={(e) => setLongUrl(e.target.value)}
              placeholder="https://example.com/very-long-url?with=params"
              className="h-11 ltr text-left"
              required
            />
          </div>
          <div>
            <Label htmlFor="custom_slug" className="text-sm font-bold text-stone-700 mb-1.5 block">
              slug מותאם (אופציונלי)
            </Label>
            <Input
              id="custom_slug"
              data-testid="short-link-custom-slug"
              value={customSlug}
              onChange={(e) => setCustomSlug(e.target.value.toLowerCase().replace(/[^a-z0-9\-_]/g, ""))}
              placeholder="lp-summer"
              className="h-11 ltr text-left"
            />
            <p className="text-xs text-stone-500 mt-1">a-z, 0-9, -, _ (2-40). ריק = יצירה אוטומטית של 6 תווים.</p>
          </div>
          <div>
            <Label htmlFor="title" className="text-sm font-bold text-stone-700 mb-1.5 block">
              תיאור פנימי (אופציונלי)
            </Label>
            <Input
              id="title"
              data-testid="short-link-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="קמפיין קיץ 2026 - וואטסאפ"
              className="h-11"
              maxLength={120}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            type="submit"
            data-testid="short-link-create-btn"
            disabled={creating}
            className="h-11 px-6 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold"
          >
            {creating ? "יוצר..." : "צור קיצור"}
          </Button>
        </div>
      </form>

      {/* Search + list */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="p-4 md:p-5 border-b border-stone-200 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="short-link-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לפי slug, URL, תיאור..."
              className="h-10 pr-9"
            />
          </div>
          <span className="text-xs text-stone-500 font-bold">
            {filtered.length}/{items.length}
          </span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-stone-400 text-sm">טוען...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-stone-400 text-sm">
            {items.length === 0 ? "עדיין לא נוצרו קיצורים. צור את הראשון למעלה." : "אין תוצאות."}
          </div>
        ) : (
          <ul className="divide-y divide-stone-100" data-testid="short-link-list">
            {filtered.map((link) => (
              <LinkRow
                key={link.id}
                link={link}
                onCopy={onCopy}
                onToggleActive={onToggleActive}
                onDelete={onDelete}
                copied={copiedSlug === link.slug}
              />
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm" data-testid={testId}>
      <div className="text-xs font-bold text-stone-500 mb-1">{label}</div>
      <div className="text-2xl font-[800] text-brand-charcoal">{value.toLocaleString("he-IL")}</div>
    </div>
  );
}

function LinkRow({ link, onCopy, onToggleActive, onDelete, copied }) {
  return (
    <li className="p-4 md:p-5 hover:bg-stone-50/60 transition" data-testid={`short-link-row-${link.slug}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        {/* Slug + short URL */}
        <div className="flex-1 min-w-0">
          {link.title ? (
            <div className="text-sm font-bold text-brand-charcoal mb-1 truncate">{link.title}</div>
          ) : null}
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-sm font-bold text-brand-amber bg-amber-50 px-2.5 py-1 rounded-lg" dir="ltr">
              /s/{link.slug}
            </code>
            {!link.is_active && (
              <span className="text-[10px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
                מושבת
              </span>
            )}
          </div>
          <a
            href={link.long_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-stone-500 hover:text-stone-700 truncate mt-1 inline-flex items-center gap-1 max-w-full"
            dir="ltr"
            title={link.long_url}
          >
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{link.long_url}</span>
          </a>
        </div>

        {/* Stats */}
        <div className="flex md:flex-col items-center md:items-end gap-4 md:gap-0">
          <div className="text-center md:text-left">
            <div className="text-lg font-[800] text-brand-charcoal leading-tight" data-testid={`clicks-${link.slug}`}>
              {(link.click_count || 0).toLocaleString("he-IL")}
            </div>
            <div className="text-[10px] text-stone-500 font-bold">קליקים</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => onCopy(link.short_url, link.slug)}
            data-testid={`copy-${link.slug}`}
            title="העתק את הקיצור"
            className="w-9 h-9 rounded-lg bg-stone-100 hover:bg-brand-amber hover:text-white text-stone-600 flex items-center justify-center transition"
          >
            {copied ? <ClipboardCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
          <div className="flex items-center gap-1.5" title={link.is_active ? "פעיל" : "מושבת"}>
            <Switch
              data-testid={`toggle-${link.slug}`}
              checked={link.is_active}
              onCheckedChange={() => onToggleActive(link)}
            />
            <Power className={`w-3.5 h-3.5 ${link.is_active ? "text-emerald-600" : "text-stone-400"}`} />
          </div>
          <button
            type="button"
            onClick={() => onDelete(link)}
            data-testid={`delete-${link.slug}`}
            title="מחק"
            className="w-9 h-9 rounded-lg bg-stone-100 hover:bg-rose-500 hover:text-white text-stone-600 flex items-center justify-center transition"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </li>
  );
}
